import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { Plus, Play, Pencil, Trash2, X, Search } from 'lucide-react';

export function Modal({ title, children, onClose }) {
  return (
    <div className="modal-backdrop" onClick={e => e.target===e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h3>{title}</h3>
          <button className="modal-close" onClick={onClose}><X size={18}/></button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

// ── Customer Search Dropdown ──────────────────────────────────
export function CustomerSearch({ onSelect, excludeIds = [], placeholder = 'Search customer…' }) {
  const [q,       setQ]       = useState('');
  const [results, setResults] = useState([]);
  const [allList, setAllList] = useState([]);   // full list for dropdown
  const [open,    setOpen]    = useState(false);
  const wrapRef = React.useRef(null);

  // Load full customer list once on mount
  useEffect(() => {
    api.get('/customers').then(res => {
      setAllList(Array.isArray(res.data) ? res.data : []);
    }).catch(() => {});
  }, []);

  // Filter by search query
  useEffect(() => {
    const base = q
      ? allList.filter(c =>
          c.name.toLowerCase().includes(q.toLowerCase()) ||
          c.mobile.includes(q)
        )
      : allList;
    setResults(base.filter(c => !excludeIds.includes(c.id)));
  }, [q, allList, excludeIds]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const pick = (c) => {
    if (c.is_occupied) { toast.error(`${c.name} is already in another session`); return; }
    onSelect(c);
    setQ(''); setOpen(false);
  };

  const displayed = results.slice(0, 50);

  return (
    <div ref={wrapRef} style={{ position:'relative' }}>
      <div style={{ position:'relative' }}>
        <Search size={14} style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'#94a3b8', pointerEvents:'none' }}/>
        <input
          style={{ paddingLeft:32, width:'100%', padding:'9px 36px 9px 32px', border:'1px solid #e2e8f0', borderRadius:8, fontSize:14, boxSizing:'border-box' }}
          placeholder={placeholder}
          value={q}
          onChange={e => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          autoComplete="off"
        />
        {/* Dropdown arrow */}
        <span
          onClick={() => setOpen(o => !o)}
          style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', cursor:'pointer', color:'#94a3b8', fontSize:12 }}>
          ▾
        </span>
      </div>

      {open && (
        <div style={{ position:'absolute', zIndex:1000, top:'100%', left:0, right:0, background:'#fff', border:'1px solid #e2e8f0', borderRadius:8, boxShadow:'0 8px 28px rgba(0,0,0,.14)', maxHeight:260, overflowY:'auto', marginTop:4 }}>
          {displayed.length === 0 ? (
            <div style={{ padding:'16px', textAlign:'center', color:'#94a3b8', fontSize:13 }}>
              {q ? 'No customers found' : 'No customers yet — add from Customers menu'}
            </div>
          ) : (
            displayed.map(c => (
              <div key={c.id}
                onClick={() => pick(c)}
                style={{
                  padding:'10px 14px',
                  cursor: c.is_occupied ? 'not-allowed' : 'pointer',
                  background: c.is_occupied ? '#fef2f2' : '',
                  display:'flex', justifyContent:'space-between', alignItems:'center',
                  borderBottom:'1px solid #f8fafc',
                  transition:'background .12s'
                }}
                onMouseEnter={e => { if (!c.is_occupied) e.currentTarget.style.background='#f0f9ff'; }}
                onMouseLeave={e => { e.currentTarget.style.background = c.is_occupied ? '#fef2f2' : ''; }}
              >
                <div>
                  <div style={{ fontWeight:600, fontSize:14 }}>{c.name}</div>
                  <div style={{ fontSize:12, color:'#94a3b8' }}>{c.mobile}</div>
                </div>
                {c.is_occupied
                  ? <span style={{ fontSize:11, color:'#ef4444', background:'#fee2e2', padding:'2px 8px', borderRadius:12, flexShrink:0 }}>Playing</span>
                  : <span style={{ fontSize:11, color:'#22c55e', background:'#dcfce7', padding:'2px 8px', borderRadius:12, flexShrink:0 }}>Available</span>}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default function Tables() {
  const [tables,   setTables]   = useState([]);
  const [modal,    setModal]    = useState(null);
  const [selected, setSelected] = useState(null);
  const [form,     setForm]     = useState({ name:'', hourly_rate:100 });
  const [players,  setPlayers]  = useState([]);   // customers for new session
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try { const r = await api.get('/tables'); setTables(r.data); } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd   = () => { setForm({ name:'', hourly_rate:100 }); setModal('add'); };
  const openEdit  = (t) => { setSelected(t); setForm({ name:t.name, hourly_rate:t.hourly_rate }); setModal('edit'); };
  const openStart = (t) => { setSelected(t); setPlayers([]); setModal('start'); };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Table name required');
    try {
      if (modal === 'add') { await api.post('/tables', form); toast.success('Table added'); }
      else { await api.put('/tables?id='+selected.id, form); toast.success('Updated'); }
      setModal(null); load();
    } catch (e) { toast.error(e.response?.data?.error || 'Error'); }
  };

  const del = async (t) => {
    if (!window.confirm(`Delete ${t.name}?`)) return;
    try { await api.delete('/tables?id='+t.id); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Error'); }
  };

  const addPlayer  = (c) => setPlayers(p => [...p, c]);
  const dropPlayer = (id) => setPlayers(p => p.filter(c => c.id !== id));

  const startSession = async () => {
    if (players.length === 0) return toast.error('Add at least one customer');
    try {
      const res = await api.post('/sessions?action=start', {
        table_id:     selected.id,
        customer_ids: players.map(c => c.id),
      });
      toast.success('Session started!');
      setModal(null);
      navigate('/session/' + res.data.session_id);
    } catch (e) { toast.error(e.response?.data?.error || 'Error'); }
  };

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Tables</h2>
        <button className="btn btn-primary" onClick={openAdd}><Plus size={16}/> Add Table</button>
      </div>

      <div className="tables-grid">
        {tables.map(t => (
          <div key={t.id} className={`table-card status-${t.status}`}>
            <div className="table-card-header">
              <h3>{t.name}</h3>
              <span className={`badge badge-${t.status}`}>{t.status}</span>
            </div>
            <div className="table-card-rate">₹{t.hourly_rate}/hr</div>
            {t.session_id && (
              <div className="table-card-guest">
                {t.player_names || t.guest_name || 'Session active'}
              </div>
            )}
            <div className="table-card-actions">
              {t.status === 'available'
                ? <button className="btn btn-success btn-sm" onClick={() => openStart(t)}><Play size={14}/> Start Session</button>
                : t.session_id
                  ? <button className="btn btn-primary btn-sm" onClick={() => navigate('/session/'+t.session_id)}>View Session</button>
                  : null
              }
              <button className="btn btn-outline btn-sm" onClick={() => openEdit(t)}><Pencil size={14}/></button>
              {t.status !== 'occupied' && (
                <button className="btn btn-danger btn-sm" onClick={() => del(t)}><Trash2 size={14}/></button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Add/Edit Table */}
      {(modal==='add'||modal==='edit') && (
        <Modal title={modal==='add'?'Add Table':'Edit Table'} onClose={() => setModal(null)}>
          <div className="form-group">
            <label>Table Name</label>
            <input value={form.name} onChange={e => setForm(f=>({...f,name:e.target.value}))} placeholder="Table 1"/>
          </div>
          <div className="form-group">
            <label>Hourly Rate (₹)</label>
            <input type="number" value={form.hourly_rate} onChange={e => setForm(f=>({...f,hourly_rate:e.target.value}))}/>
          </div>
          <div className="modal-actions">
            <button className="btn btn-outline" onClick={() => setModal(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={save}>Save</button>
          </div>
        </Modal>
      )}

      {/* Start Session — customer selection */}
      {modal==='start' && (
        <Modal title={`Start Session — ${selected?.name}`} onClose={() => setModal(null)}>
          <div style={{ marginBottom:8, fontSize:13, color:'#64748b' }}>
            Rate: <strong>₹{selected?.hourly_rate}/hr</strong>
          </div>

          {/* Selected players */}
          {players.length > 0 && (
            <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:12 }}>
              {players.map(c => (
                <div key={c.id} style={{ display:'flex', alignItems:'center', gap:6, background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:20, padding:'4px 12px', fontSize:13 }}>
                  <span style={{ fontWeight:600 }}>{c.name}</span>
                  <span style={{ color:'#94a3b8', fontSize:11 }}>{c.mobile}</span>
                  <button onClick={() => dropPlayer(c.id)} style={{ background:'none', border:'none', cursor:'pointer', color:'#94a3b8', padding:0, display:'flex' }}><X size={14}/></button>
                </div>
              ))}
            </div>
          )}

          <div className="form-group">
            <label>Search & Add Customers {players.length > 0 && `(${players.length} added)`}</label>
            <CustomerSearch
              excludeIds={players.map(c => c.id)}
              onSelect={addPlayer}
              placeholder="Type name or mobile to search…"
            />
          </div>

          {players.length === 0 && (
            <div style={{ background:'#fef9c3', border:'1px solid #fde68a', borderRadius:8, padding:'10px 14px', fontSize:13, color:'#92400e', marginTop:8 }}>
              ⚠️ Add at least one customer to start the session
            </div>
          )}

          <div className="modal-actions">
            <button className="btn btn-outline" onClick={() => setModal(null)}>Cancel</button>
            <button className="btn btn-success" onClick={startSession} disabled={players.length===0}>
              <Play size={14}/> Start ({players.length} player{players.length!==1?'s':''})
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
